/**
 * The desktop rail's sticky stack, top to bottom: the under-nav table of contents (UnderNavBlock,
 * `position: fixed` over the rail column), the ConversationRailCard strip, the sticky rail ad.
 *
 * The TOC lives outside the rail, so it publishes `--rail-toc-stack` on `<html>` (the only common
 * ancestor of a viewport-fixed bar and the rail; only PostNavigation reads it): the viewport line
 * under its closed box plus the 16px gap, in px — 0 when it is absent or hidden. PostNavigation
 * turns that into `--rail-stack-top`, the card's sticky top; the ads add `--rail-pinned-offset`.
 */
export const RAIL_TOC_STACK_VAR = '--rail-toc-stack'

/** Gap between two stacked layers (TOC → strip, strip → ad), px. */
export const RAIL_STACK_GAP_PX = 16

/** Cap for a TOC first measured while open: its list overlays the strip, it never pushes it. */
export const TOC_CLOSED_MAX_REM = 7.5

/**
 * The TOC's closed height: the smallest non-zero height seen. Its open/close is a height
 * transition, so a ResizeObserver sees every in-between frame — only the minimum is the closed
 * box. Never seen yet: the measured height, capped (an open list overlays, it never pushes).
 */
export function tocClosedHeight(measured: number, lastClosed: number, capPx: number): number {
  if (measured <= 0) {
    return lastClosed
  }
  if (lastClosed > 0) {
    return Math.min(lastClosed, measured)
  }
  return Math.min(measured, capPx)
}

/**
 * Whether the under-nav TOC is on screen, mirroring UnderNavBlock's classes: with a best-buy
 * panel the TOC shows exactly while the panel does; without one, while the TOC is not hidden
 * (header shown, dealbar sticky, or not scrolled a screen yet).
 */
export function underNavTocShown(input: {
  rendered: boolean
  hasPanel: boolean
  panelVisible: boolean
  tocHidden: boolean
}): boolean {
  if (!input.rendered) {
    return false
  }
  return input.hasPanel ? input.panelVisible : !input.tocHidden
}

/** `--rail-toc-stack` in px: the line under the closed TOC plus the gap; 0 when not shown. */
export function railTocStack(input: { shown: boolean; top: number; closedHeight: number }): number {
  if (!input.shown || input.closedHeight <= 0) {
    return 0
  }
  return Math.round(input.top + input.closedHeight + RAIL_STACK_GAP_PX)
}
